import type { MyChronMapPoint } from '@kangaroo/shared';

import { TELEMETRY_FILE_LIST_STALE_MS } from './telemetryCachePolicy';

const STORAGE_KEY_PREFIX = 'paddiq-circuit-outline-v1';

export type CircuitOutlineCacheEntry = {
  savedAt: number;
  updatedAt: string | null;
  points: MyChronMapPoint[];
};

const memoryCache = new Map<string, CircuitOutlineCacheEntry>();

function storageKey(contributionId: string) {
  return `${STORAGE_KEY_PREFIX}:${contributionId}`;
}

function isFresh(entry: CircuitOutlineCacheEntry, updatedAt: string | null) {
  if (Date.now() - entry.savedAt > TELEMETRY_FILE_LIST_STALE_MS) {
    return false;
  }

  return entry.updatedAt === updatedAt;
}

export function readCircuitOutlineCache(
  contributionId: string,
  updatedAt: string | null,
): MyChronMapPoint[] | null {
  const cached = memoryCache.get(contributionId);
  if (cached && isFresh(cached, updatedAt) && cached.points.length >= 2) {
    return cached.points;
  }

  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(storageKey(contributionId));
  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as CircuitOutlineCacheEntry;
    if (!parsed?.savedAt || !Array.isArray(parsed.points) || !isFresh(parsed, updatedAt)) {
      window.localStorage.removeItem(storageKey(contributionId));
      return null;
    }

    if (parsed.points.length < 2) {
      return null;
    }

    memoryCache.set(contributionId, parsed);
    return parsed.points;
  } catch {
    window.localStorage.removeItem(storageKey(contributionId));
    return null;
  }
}

export function writeCircuitOutlineCache(
  contributionId: string,
  updatedAt: string | null,
  points: MyChronMapPoint[],
) {
  if (points.length < 2) {
    return;
  }

  const entry: CircuitOutlineCacheEntry = {
    savedAt: Date.now(),
    updatedAt,
    points,
  };

  memoryCache.set(contributionId, entry);

  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(storageKey(contributionId), JSON.stringify(entry));
  } catch {
    // Ignore quota errors — memory cache still helps for this session.
  }
}

export function invalidateCircuitOutlineCache(contributionId?: string) {
  if (contributionId) {
    memoryCache.delete(contributionId);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(storageKey(contributionId));
    }
    return;
  }

  memoryCache.clear();
  if (typeof window === 'undefined') {
    return;
  }

  for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(`${STORAGE_KEY_PREFIX}:`)) {
      window.localStorage.removeItem(key);
    }
  }
}
